import { createTheme, responsiveFontSizes } from '@material-ui/core';
import { palette } from './palette';

const CustomTheme = responsiveFontSizes(
  createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    palette,
    layout: {
      contentWidth: 1600,
    },
    typography: {
      fontFamily: 'Helvetica, Arial, sans-serif',
      h1: {
        fontWeight: 500,
        letterSpacing: 0.75
      },
      h2: {
        fontSize: 55,
        fontWeight: 500,
        letterSpacing: 0.75
      },
      h3: {
        fontWeight: 500,
        letterSpacing: 0.75
      },
      h4: {
        fontWeight: 500,
        letterSpacing: 0.75
      },
      h5: {
        fontWeight: 500,
        letterSpacing: 0.75
      },
      h6: {
        fontWeight: 500,
        letterSpacing: 0.75
      }
    },
    zIndex: {
      appBar: 1200,
      drawer: 1100,
    },
    customValues: {
      borderRadius: 10,
    },
    overrides: {
      MuiCssBaseline: {
        '@global': {
          html: {
            WebkitFontSmoothing: 'auto',
          },
          body: {
            fontFamily: [
              'Helvetica',
              '-apple-system',
              'BlinkMacSystemFont',
              'Segoe UI',
              'Roboto',
              'Oxygen',
              'Ubuntu',
              'Cantarell',
              'Fira Sans',
              'Droid Sans',
              'Helvetica Neue',
              'sans-serif'
            ].join(', '),
          },
          a: {
            textDecoration: 'none',
            color: palette.primary.main,
          },
          '.bold': {
            fontWeight: 700,
          },
          '.semibold': {
            fontWeight: 600,
          },
          '.fullHeight': {
            height: '100%',
          },
          '.success': {
            color: `${palette.text.success} !important`,
          },
          '.MuiSvgIcon-colorSecondary': {
            color: `${palette.text.primary} !important`,
          },
          '.recharts-legend-item': {
            marginTop: 15,
            marginBottom: 15,
          },
          ol: {
            paddingLeft: 32,
          },
          '.react-multiple-carousel__arrow--left': {
            left: 0
          },
          '.react-multiple-carousel__arrow--right': {
            right: 0
          },
          '.react-multiple-carousel__arrow--left::before': {
            content: '"\\2190"'
          },
          '.react-multiple-carousel__arrow--right::before': {
            content: '"\\2192"'
          },
          '.react-multiple-carousel__arrow': {
            background: palette.primary.main
          },
          '.react-multi-carousel-dot button': {
            border: 'none',
            height: 6,
            width: 35,
            borderRadius: 0,
            transition: 'all 0.3s',
            background: palette.text.disabled,

            '&:hover:active': {
              background: palette.primary.main,
            },
          },
          '.react-multi-carousel-dot--active button': {
            width: 70,
            backgroundColor: palette.secondary.main,
          },
          '.plan-selection-container .react-multi-carousel-dot button': {
            background: palette.primary.main,
            opacity: '0.2'
          },
          '.plan-selection-container .react-multi-carousel-dot--active button': {
            backgroundColor: palette.primary.main,
            opacity: '1'
          },
          '.hero-banner': {
            display: 'none',
          },
          '.planCard': {
            borderRadius: '0px 24px !important',
            borderColor: 'none !important',
          },
          '.wp-content': {
            fontSize: '0.93rem',
            lineHeight: 1.5,
            color: palette.text.primary,

            '& h6': {
              fontSize: 16,
              margin: '0 0 8px',
            },
            '& p': {
              margin: '20px 0',
            },
            '& li': {
              margin: '20px 0',

              '& li': {
                listStyleType: 'lower-roman'
              }
            },
            '& .heading': {
              fontSize: '2rem',
              margin: '40px 0 0',
            },
            '& .subHeading': {
              fontSize: '1.5rem',
              margin: '35px 0 0',
            },
            '& .sectionHeading': {
              fontSize: '1.25rem',
              margin: '35px 0 0',
            },
          },
        },
      },
      MuiAccordionSummary: {
        expandIcon: {
          '& .collapseIcon': {
            display: 'none',
          },
          '&$expanded': {
            '& .collapseIcon': {
              display: 'block',
            },
            '& .expandIcon': {
              display: 'none',
            },
          },
          '.mainContainer & svg': {
            color: `${palette.icon.tertiary} !important`,
          },
        },
      },
      MuiAlert: {
        outlinedSuccess: {
          background: palette.background.success,
        },
        outlinedError: {
          background: palette.background.error,
        },
      },
      MuiButton: {
        root: {
          fontWeight: 700,
          textTransform: 'none',
          letterSpacing: 0.75,
          borderRadius: 10,
          padding: 13,
          boxShadow: 'none !important',

          '&.pill': {
            borderRadius: 30,
          },

          '&.primary-contrast': {
            background: palette.primary.contrastText,
            color: palette.primary.main,
          },
        },
        '&:hover': {
          boxShadow: 'none !important'
        },
        outlined: {
          padding: 13,
          borderWidth: 2,
          color: palette.text.primary,
          borderColor: palette.primary.main,
          background: palette.background.default,
          '&:hover': {
            background: palette.text.highlight,
            borderColor: palette.primary.hover,
          },
          '&.timeSlot': {
            borderColor: `${palette.border.primary} !important`,
            color: `${palette.text.detail} !important`,

            '&:hover': {
              backgroundColor: `${palette.brand.main} !important`,
              borderColor: `${palette.brand.main} !important`,
              color: `${palette.text.highlight} !important`,
            },
          },
        },
        contained: {
          '&.timeSlot': {
            backgroundColor: `${palette.brand.main} !important`,
            borderColor: `${palette.brand.main} !important`,
            color: `${palette.text.highlight} !important`,
          },
        },
        containedPrimary: {
          color: palette.primary.contrastText,
          '&:hover': {
            backgroundColor: palette.primary.hover,
            color: palette.text.highlight,
          },
          '[role="dialog"]:not(.gm-style-iw) &': {
            borderRadius: '10px !important',
            padding: '16px 20px',
            fontSize: '18px !important',
            fontWeight: 400,
            lineHeight: '20px',
            letterSpacing: '-0.36px',

            '&:not(.Mui-disabled)': {
              backgroundColor: `${palette.brand.main} !important`,
              color: `${palette.text.highlight} !important`,

              '&:hover': {
                backgroundColor: `${palette.brand.hover} !important`,
              },
            },
          },
        },
        textPrimary: {
          '[role="dialog"]:not(.gm-style-iw) &': {
            color: `${palette.brand.main} !important`,
            fontSize: '18px !important',
            fontWeight: 400,
            letterSpacing: '-0.36px',
          },
        },
        outlinedPrimary: {
          color: palette.text.primary,
          borderColor: palette.primary.main,
          backgroundColor: palette.text.highlight,
          '&:hover': {
            backgroundColor: palette.text.highlight,
            borderColor: palette.primary.hover,
          }
        },
        outlinedSecondary: {
          color: palette.text.primary,
          borderColor: palette.primary.main,
          '&:hover': {
            background: palette.text.highlight,
            borderColor: palette.primary.hover,
          }
        },
        startIcon: {
          marginLeft: 0,
        },
      },
      MuiDrawer: {
        paper: {
          width: 450,
          maxWidth: '100%',
        },
      },
      MuiIconButton: {
        colorPrimary: {
          color: palette.primary.main,
        },
        colorSecondary: {
          backgroundColor: palette.background.paper,

          '&:hover': {
            backgroundColor: palette.background.level2,
          },
        },
      },
      MuiButtonGroup: {
        root: {
          border: `1px solid ${palette.primary.main}`,
          padding: 4,
          borderRadius: 20,
        },
        grouped: {
          borderRadius: 20,
          fontSize: '0.7rem',
        },
        groupedHorizontal: {
          '&:not(:first-child)': {
            borderTopLeftRadius: 20,
            borderBottomLeftRadius: 20,
          },
          '&:not(:last-child)': {
            borderTopRightRadius: 20,
            borderBottomRightRadius: 20,
          },
        },
        groupedTextHorizontal: {
          '&:not(:last-child)': {
            borderRight: 0,
          },
        },
      },
      MuiInputBase: {
        input: {
          scrollMargin: '100px',
        },
      },
      MuiInputAdornment: {
        positionEnd: {
          height: '100%',
          position: 'relative',
          background: palette.background.paper,
        },
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: 10,
          '.mainContainer &.Mui-focused fieldset': {
            borderColor: `${palette.brand.main} !important`,
          },
          '&:has(.select-custom-field)': {
            '& fieldset': {
              borderColor: `${palette.border.subtle} !important`,
              borderWidth: '1px !important',
            },
            '&.Mui-focused fieldset': {
              borderColor: `${palette.brand.main} !important`,
              borderWidth: '1px !important',
            },
          },
        },
      },
      MuiPaper: {
        root: {
          '&.widget': {
            padding: 24,
          },
        },
        rounded: {
          borderRadius: 10,
        },
      },
      MuiCardContent: {
        root: {
          padding: 24,
          '.cardRoot & > svg': {
            fontSize: '30px !important',
            color: `${palette.icon.tertiary} !important`,
          },
        },
      },
      MuiSelect: {
        nativeInput: {
          scrollMargin: '150px',
        },
      },
      MuiSnackbar: {
        anchorOriginTopCenter: {
          top: '85px !important',
        },
      },
      MuiTab: {
        root: {
          '&.compact': {
            padding: '12px 16px'
          },
        },
      },
      MuiTabs: {
        root: {
          '&.subscription-tabs': {
            '& .MuiTab-wrapper': {
              textTransform: 'none',
            },
            '& .MuiTabs-scroller': {
              display: 'flex',
              justifyContent: 'center',
            },
            '& .MuiTabs-flexContainer': {
              background: palette.primary.contrastText,
              borderRadius: '2rem',

              '& .MuiTab-root': {
              },
              '& .Mui-selected': {
                background: palette.secondary.main,
                color: palette.secondary.contrastText,
                borderRadius: '2rem'
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none'
            }
          }
        },
        vertical: {
          '& .MuiTab-root': {
            paddingLeft: 20,
          },
          '& .MuiTab-wrapper': {
            alignItems: 'flex-start',
          },
        },
      },
      MuiTabScrollButton: {
        root: {
          width: 25,
          '&$disabled': {
            width: 0,
            transition: 'all 0.2s',
          },
        },
      },
      PrivateTabIndicator: {
        vertical: {
          left: 0,
        },
        root: {
          height: 3,
        },
      },
      MuiTableRow: {
        root: {
          '&:nth-of-type(even)': {
            background: palette.background.tableAlternate,
          },
        },
      },
      MuiTableCell: {
        root: {
          borderBottom: 0,
          background: palette.text.highlight,
          color: palette.text.highlight,
        },
        head: {
          background: palette.background.highlight,
          padding: '12px 16px',
        },
      },
      MUIDataTableHeadCell: {
        fixedHeader: {
          backgroundColor: palette.background.tableHead,
        },
        data: {
          fontSize: '0.875rem',
          fontWeight: 500,
        },
      },
      MuiTableSortLabel: {
        root: {
          marginTop: 2,
        },
      },
      MuiTypography: {
        body1: {
          '@media(max-width: 600px)': {
            fontSize: '0.9rem',
          },
        },
        root: {
          '.pearlcares-container &': {
            fontFamily: 'Helvetica, Arial, sans-serif',
          },
          '.radio-card &': {
            fontSize: '18px !important',
            lineHeight: '24px !important',
            letterSpacing: '-0.36px',
            color: `${palette.text.normal} !important`,
          },
          '.radio-card.radio-card-selected &': {
            color: `${palette.brand.main} !important`,
          },
        },
        h4: {
          '.mainContainer &': {
            fontSize: 32,
            fontWeight: 500,
            lineHeight: '36px',
            letterSpacing: '-1.28px',
            color: palette.text.heading,
          },
        },
        h5: {
          '.detail-card &': {
            fontSize: 18,
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: '-0.36px',
            color: palette.text.detail,
          },
        },
        colorTextPrimary: {
          '.cardRoot &': {
            color: palette.text.primary,
          },
          '.cardRoot:hover &': {
            color: `${palette.text.primary} !important`,
          },
        },
      },
      MuiDivider: {
        root: {
          '.detail-card &': {
            backgroundColor: palette.border.card,
          },
        },
      },
      MuiSvgIcon: {
        root: {
          '.slot-calendar-button &': {
            color: `${palette.brand.main} !important`,
          },
        },
      },
      MuiCheckbox: {
        root: {
          '.mainContainer &.Mui-checked': {
            color: `${palette.brand.main} !important`,
          },
        },
      },
      MuiRadio: {
        root: {
          '.radio-card &': {
            '& > span:first-child': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              width: '20px !important',
              height: '20px !important',
              borderRadius: '50%',
              border: `1px solid ${palette.border.subtle}`,
            },
            '& svg': {
              display: 'none !important',
            },
          },
          '.radio-card &.Mui-checked > span:first-child': {
            backgroundColor: `${palette.brand.main} !important`,
            borderColor: `${palette.brand.main} !important`,

            '&::after': {
              content: '""',
              display: 'block',
              width: 7.5,
              height: 7.5,
              borderRadius: '50%',
              backgroundColor: palette.text.highlight,
            },
          },
        },
      },
      MuiFormLabel: {
        root: {
          '.mainContainer &.Mui-focused': {
            color: `${palette.brand.main} !important`,
          },
        },
      },
      MuiSwitch: {
        switchBase: {
          '.pearlcares-container &.Mui-checked': {
            color: palette.primary.main,

            '& + $track': {
              backgroundColor: palette.background.default,
              borderColor: palette.primary.main,
            },
          },
        },
      },
      MuiPickersDay: {
        current: {
          color: palette.text.primary
        },
        daySelected: {
          backgroundColor: palette.primary.hover,
          color: palette.text.highlight,

          '&:hover': {
            backgroundColor: palette.primary.hover,
          }
        }
      },
      MuiLinearProgress: {
        barColorSecondary: {
          backgroundColor: palette.verticalBar.unlimited
        }
      },
    },
  }),
);

export default CustomTheme;
